import { useEffect, useRef, useState, createContext, useContext, ReactNode } from 'react';
import React from 'react';
import { driverSupabase } from '@/integrations/supabase/driver-client';
import { useDriverAuth } from '@/hooks/useDriverAuth';
import { useLocationGate } from '@/hooks/useLocationGate';

export interface DeviceStatusPayload {
  wifi_on: boolean | null;
  mobile_data_on: boolean | null;
  gps_enabled: boolean;
  ts: number;
}

interface DeviceStatusContextValue {
  wifiOn: boolean | null;
  mobileDataOn: boolean | null;
  gpsOn: boolean;
  anyOff: boolean;
}

const DeviceStatusContext = createContext<DeviceStatusContextValue>({
  wifiOn: null,
  mobileDataOn: null,
  gpsOn: true,
  anyOff: false,
});

export function useDeviceStatus() {
  return useContext(DeviceStatusContext);
}

export function DeviceStatusProvider({ children }: { children: ReactNode }) {
  const { driver } = useDriverAuth();
  const { status: locStatus } = useLocationGate();

  const [wifiOn, setWifiOn] = useState<boolean | null>(null);
  const [mobileDataOn, setMobileDataOn] = useState<boolean | null>(null);

  const channelRef = useRef<any>(null);
  const lastPayloadRef = useRef<string>('');
  const networkListenerRef = useRef<{ remove: () => void } | null>(null);

  const gpsOn = locStatus === 'granted' || locStatus === 'checking';

  // Subscribe to network changes (wifi vs cellular)
  useEffect(() => {
    let cancelled = false;

    async function setupNetwork() {
      try {
        const { Capacitor } = await import('@capacitor/core');
        const isNative = Capacitor.isNativePlatform();

        if (isNative) {
          const { Network } = await import('@capacitor/network');
          const apply = (s: { connected: boolean; connectionType: string }) => {
            if (cancelled) return;
            const type = s.connectionType;
            setWifiOn(s.connected && type === 'wifi');
            setMobileDataOn(s.connected && type === 'cellular');
          };
          const status = await Network.getStatus();
          apply(status);
          const handle = await Network.addListener('networkStatusChange', apply);
          networkListenerRef.current = handle;
        } else {
          // Web: cannot distinguish wifi vs cellular reliably
          const update = () => {
            if (cancelled) return;
            const online = navigator.onLine;
            const conn = (navigator as any).connection;
            const type = conn?.type;
            if (type === 'wifi') {
              setWifiOn(online);
              setMobileDataOn(false);
            } else if (type === 'cellular') {
              setWifiOn(false);
              setMobileDataOn(online);
            } else {
              // Unknown — report null so admin sees "unknown" rather than false
              setWifiOn(online ? null : false);
              setMobileDataOn(online ? null : false);
            }
          };
          update();
          window.addEventListener('online', update);
          window.addEventListener('offline', update);
          networkListenerRef.current = {
            remove: () => {
              window.removeEventListener('online', update);
              window.removeEventListener('offline', update);
            },
          };
        }
      } catch {}
    }

    setupNetwork();
    return () => {
      cancelled = true;
      networkListenerRef.current?.remove();
      networkListenerRef.current = null;
    };
  }, []);

  // Join broadcast channel scoped to driver
  useEffect(() => {
    if (!driver?.id) return;

    const channelName = `device-status-${driver.id}`;
    const channel = driverSupabase.channel(channelName, {
      config: { broadcast: { self: true, ack: false } },
    });

    channel.subscribe((status) => {
      console.debug('[DeviceStatus] channel status:', status);
    });

    channelRef.current = channel;

    return () => {
      try {
        driverSupabase.removeChannel(channel);
      } catch {}
      channelRef.current = null;
    };
  }, [driver?.id]);

  // Broadcast on any status change
  useEffect(() => {
    if (!channelRef.current) return;
    const payload: DeviceStatusPayload = {
      wifi_on: wifiOn,
      mobile_data_on: mobileDataOn,
      gps_enabled: gpsOn,
      ts: Date.now(),
    };
    const sig = `${payload.wifi_on}|${payload.mobile_data_on}|${payload.gps_enabled}`;
    if (sig === lastPayloadRef.current) return;
    lastPayloadRef.current = sig;

    console.debug('[DeviceStatus] broadcasting:', payload);
    channelRef.current.send({
      type: 'broadcast',
      event: 'status',
      payload,
    }).catch(() => {});
  }, [wifiOn, mobileDataOn, gpsOn]);

  const anyOff = !gpsOn || wifiOn === false || mobileDataOn === false;

  return React.createElement(
    DeviceStatusContext.Provider,
    { value: { wifiOn, mobileDataOn, gpsOn, anyOff } },
    children
  );
}
